/**
 * /admin/data-exports — team feedback P12 (Pedro 2026-10-10). Admin-only, like every bulk
 * export (C-335).
 *
 * Four Excel downloads — Agreements, Persons, Accounts, Policies — each with every record
 * that isn't deleted and all of its fields; linked records show by name, and a few summary
 * columns lead each sheet. Every download is recorded in the audit log (which list, how
 * many rows — never the data). Backend: routers/data_exports.py.
 */
import { Button, Card, Container, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload } from '@tabler/icons-react';
import { useState } from 'react';

import { downloadBlob } from '../api/iaaExports';
import { RequireAccess } from '../components/RequireAccess';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

type Kind = 'agreements' | 'persons' | 'accounts' | 'policies';

const LISTS: { kind: Kind; title: string; detail: string }[] = [
  {
    kind: 'agreements', title: 'Agreements',
    detail: 'Every field, led by the code, the main contact (KYC contact), the Named RMs and the holders.',
  },
  {
    kind: 'persons', title: 'Persons',
    detail: 'Every field, led by the Active / Inactive status and every role the person holds.',
  },
  {
    kind: 'accounts', title: 'Accounts',
    detail: 'Every field, led by the account’s parties; the agreement shows by its code.',
  },
  {
    kind: 'policies', title: 'Policies',
    detail: 'Every field, led by the policy’s parties; the agreement shows by its code.',
  },
];

function fileName(cd: string, fallback: string): string {
  const m = /filename="([^"]+)"/.exec(cd);
  return m ? m[1] : fallback;
}

export function DataExportsPage() {
  useDocumentTitle('Data exports');
  const [busy, setBusy] = useState<Kind | null>(null);

  async function download(kind: Kind) {
    setBusy(kind);
    try {
      const res = await fetch(`/data-exports/${kind}`, { credentials: 'include' });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error((body && typeof body.detail === 'string' && body.detail) || 'The export could not be generated.');
      }
      downloadBlob(await res.blob(), fileName(res.headers.get('Content-Disposition') ?? '', `${kind}.xlsx`));
    } catch (e) {
      notifications.show({
        color: 'red', title: 'Export failed',
        message: e instanceof Error ? e.message : 'Could not export.',
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <RequireAccess roles={['admin']} message="Data exports are restricted to Admins.">
      <Container size="xl">
        <Stack>
          <div>
            <Title order={2}>Data exports</Title>
            <Text c="dimmed" size="sm">
              Download a full list as an Excel file: every record that isn’t deleted, with all of
              its fields. Linked records show by name. Each download is recorded in the audit log
              (who, which list, how many rows).
            </Text>
          </div>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {LISTS.map((l) => (
              <Card key={l.kind} withBorder padding="lg">
                <Stack gap="sm">
                  <Title order={4}>{l.title}</Title>
                  <Text size="sm" c="dimmed">{l.detail}</Text>
                  <Button
                    leftSection={<IconDownload size={16} />}
                    onClick={() => download(l.kind)}
                    loading={busy === l.kind}
                    disabled={busy !== null && busy !== l.kind}
                    w="fit-content"
                  >
                    Download {l.title.toLowerCase()}
                  </Button>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        </Stack>
      </Container>
    </RequireAccess>
  );
}
